import {
  ReloadOutlined,
  LeftOutlined,
  RightOutlined,
  CalendarOutlined,
  ProjectOutlined,
  UnlockOutlined,
  LockOutlined,
  InfoCircleOutlined
} from "@ant-design/icons";
import { PageHeader } from "@mj/shared-ui";
import { Button, Select, Skeleton, Modal } from "antd";
import { AxiosError } from "axios";
import classNames from "classnames";
import dayjs from "dayjs";
import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import Layout from "@/components/common/Layout";
import { ForecastVsBudget } from "@/components/forecastingOverview/ForecastVsBudget";
import { TasksStageView } from "@/components/forecastingOverview/TasksStageView";
import { features } from "@/config/features";
import { useUser } from "@/contexts/UserContext";
import {
  CarryForwardModal,
  getCostTotals,
  hasAnyCost,
  ICarryForwardChoice,
  ICarryForwardState,
  ICarryForwardTask,
  ICostTotals
} from "@/features/forecast-carry-forward";
import { useProjects } from "@/hooks/businessCentral";
import {
  useGetAllMonthlyForecasts,
  useGetMonthlyForecast,
  useGetProjectCentralRepoWithOpportunityNo,
  useLockUnlockForecast
} from "@/hooks/forecasting";
import { ISelectOption } from "@/types";
import { formatNumberToGB } from "@/utils/currency";
import storage from "@/utils/storage";

interface IMonthButton {
  label: string;
  value: string;
  month: string;
  year: string;
}

// Placeholder for a month card's figure while its data loads, sized to one
// line of text so the card doesn't jump when the value arrives.
const ValueSkeleton = () => (
  <span className="inline-block h-4 w-16 rounded bg-slate-200 animate-pulse" />
);

const ForecastingOverviewPage = () => {
  const [selectedProject, setSelectedProject] =
    React.useState<ISelectOption | null>(null);
  const [selectedMonth, setSelectedMonth] = React.useState(
    dayjs().format("YYYY-MM")
  );
  const [monthOffset, setMonthOffset] = React.useState(0);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const user = useUser();
  const intendedProject = searchParams.get("project");

  const { isLoading: projectsLoading, data: projectsData } = useProjects();
  const {
    isLoading: monthForecastLoading,
    data: monthForecastData,
    error: monthForecastError
  } = useGetMonthlyForecast(
    selectedProject?.description,
    selectedMonth.slice(0, 4),
    selectedMonth.slice(-2),
    selectedProject?.opportunityNo
  );

  const { isLoading: allForecastsLoading, data: allForecastsData } =
    useGetAllMonthlyForecasts(
      selectedProject?.opportunityNo,
      selectedProject?.description
    );
  // const { isLoading: projectCentralRepoLoading, data: projectCentralRepoData } =
  //   useGetProjectCentralRepo(selectedProject?.description);

  const { isLoading: projectCentralRepoLoading, data: projectCentralRepoData } =
    useGetProjectCentralRepoWithOpportunityNo(selectedProject?.opportunityNo);

  const [isLatestVersionSelected, setIsLatestVersionSelected] = React.useState<
    null | boolean
  >(null);
  const [selectedVersion, setSelectedVersion] = React.useState<string>();
  // The newest forecast version, independent of the selected version tab.
  // TasksStageView always reflects the latest version regardless of selection.
  const [latestVersion, setLatestVersion] = React.useState<string | null>(null);

  const lockUnlockForecast = useLockUnlockForecast();

  // What the previous month's forecast offers to carry forward; `null` while
  // the modal is closed.
  const [carryForward, setCarryForward] = React.useState<{
    tasks: ICarryForwardTask[];
    costTotals: ICostTotals | null;
  } | null>(null);

  const previousMonth = dayjs(selectedMonth).subtract(1, "month");
  const previousForecast = allForecastsData?.Forecasts?.find(
    (f) => f.Month === previousMonth.format("YYYY-MM")
  )?.Forecast;

  const getIncompleteTasks = (): ICarryForwardTask[] => {
    if (
      !previousForecast?.Groundworks?.StagesAndTasks ||
      !projectCentralRepoData
    )
      return [];

    // Build a lookup from central repo for real-time task completion status
    const centralTaskStatus = new Map<string, string>();
    projectCentralRepoData.groundworks?.plots?.forEach((plot) => {
      plot.stages?.forEach((stage) => {
        stage.tasks?.forEach((task) => {
          centralTaskStatus.set(
            `${task.taskId}-${stage.stageName}-${plot.plotNo}`,
            task.currentStatus
          );
        });
      });
    });

    const incomplete: ICarryForwardTask[] = [];
    previousForecast.Groundworks.StagesAndTasks.forEach((entry) => {
      entry.Tasks?.forEach((task) => {
        const centralStatus = centralTaskStatus.get(
          `${task.taskId}-${entry.Stage}-${entry.Plot || ""}`
        );
        // Central repo is the source of truth — skip if task doesn't exist there
        if (!centralStatus) return;
        if (centralStatus !== "Completed") {
          incomplete.push({
            taskId: task.taskId,
            taskName: task.taskName,
            status: centralStatus,
            plot: entry.Plot || "",
            stage: entry.Stage
          });
        }
      });
    });

    return incomplete;
  };

  const navigateToWizard = (state?: ICarryForwardState) =>
    navigate(
      `/forecasting/${encodeURIComponent(selectedProject!.opportunityNo)}/${encodeURIComponent(selectedProject!.description)}/${selectedMonth}`,
      { state }
    );

  const handleCreateForecastClick = () => {
    const tasks = getIncompleteTasks();
    const costTotals = getCostTotals(previousForecast?.BudgetForecasts);
    const hasCosts = features.carryForwardCosts && hasAnyCost(costTotals);

    if (tasks.length === 0 && !hasCosts) {
      navigateToWizard();
      return;
    }
    setCarryForward({ tasks, costTotals: hasCosts ? costTotals : null });
  };

  const handleConfirmCarryForward = (choice: ICarryForwardChoice) => {
    const tasks = carryForward?.tasks ?? [];
    setCarryForward(null);
    navigateToWizard({
      carryForwardTasks: choice.carryForwardTasks ? tasks : [],
      copyPreviousCosts: !!carryForward?.costTotals && choice.copyCosts
    });
  };

  React.useEffect(() => {
    if (projectsData && projectsData.value?.length > 0) {
      const savedProjectNo = storage.getProject()?.split("~~")?.[0];

      if (intendedProject) {
        const target = projectsData.value.find(
          (item) => item.description === intendedProject
        );
        if (target) {
          setSelectedProject({
            key: target.no,
            value: target.no,
            label: target.no + " - " + target.description,
            no: target.no,
            opportunityNo: target.opportunityNo,
            description: target.description
          });
          storage.setProject(`${target.no}~~${target.description}`);
        }
      } else if (savedProjectNo) {
        const target = projectsData.value.find(
          (item) =>
            user?.regionCode === item.jobPostingGroup &&
            item.no === savedProjectNo
        );
        if (target) {
          setSelectedProject({
            key: target.no,
            value: target.no,
            label: target.no + " - " + target.description,
            no: target.no,
            opportunityNo: target.opportunityNo,
            description: target.description
          });
        }
      }
    }
  }, [user, projectsData, intendedProject]);

  const monthForecast404 =
    monthForecastError && (monthForecastError as AxiosError)?.status === 404;

  const monthButtons: IMonthButton[] = React.useMemo(() => {
    const buttons: IMonthButton[] = [];
    const currentDate = dayjs();

    const startDate = currentDate
      .subtract(2, "month")
      .add(monthOffset, "month");

    // Generate 6 months (past 2 + current + upcoming 3 )
    for (let i = 0; i < 6; i++) {
      const date = startDate.add(i, "month").startOf("month");
      buttons.push({
        label: date.format("MMM YYYY"),
        value: date.format("YYYY-MM"),
        month: date.format("MMMM"),
        year: date.format("YYYY")
      });
    }

    return buttons;
  }, [monthOffset]);

  const isPastMonth = (monthValue: string) => {
    const currentMonth = dayjs().startOf("month");
    const targetMonth = dayjs(monthValue).startOf("month");
    return targetMonth.isBefore(currentMonth);
  };

  const handleMonthClick = (value: string) => {
    const isPast = isPastMonth(value);
    const hasForecastForMonth = allForecastsData?.Forecasts?.find(
      (f) => f.Month === value
    );

    // Until the forecast list arrives we can't tell whether a past month has a
    // forecast, so ignore the click rather than wrongly rejecting it.
    if (isPast && allForecastsLoading) return;

    if (isPast && !hasForecastForMonth && !features.allowPastMonthForecasting) {
      toast.error("Cannot forecast for past month");
      return;
    }

    setSelectedMonth(value);
  };

  const computeMonthVariance = (
    monthForecast: NonNullable<typeof allForecastsData>["Forecasts"][number]
  ) => {
    if (!monthForecast?.Forecast || !projectCentralRepoData) return null;

    const forecast = monthForecast.Forecast;

    // 1. Forecast total from BudgetForecasts
    let forecastTotal = 0;
    if (forecast.BudgetForecasts) {
      const bf = forecast.BudgetForecasts;
      forecastTotal +=
        bf.BudgetLabourCosts?.reduce((sum, item) => sum + item.Amount, 0) ?? 0;
      forecastTotal +=
        bf.BudgetPlantCosts?.reduce((sum, item) => sum + item.Amount, 0) ?? 0;
      forecastTotal += bf.BudgetMaterialCost?.Amount ?? 0;
      forecastTotal +=
        bf.BudgetSubContractorCost?.reduce(
          (sum, item) => sum + item.Amount,
          0
        ) ?? 0;
    }

    // Add variation amounts to forecast total
    let variationAmt = 0;
    if (forecast.Variations?.length) {
      variationAmt = forecast.Variations.reduce(
        (sum, v) => sum + (v.UtilizedAmount > 0 ? v.UtilizedAmount : 0),
        0
      );
      forecastTotal += variationAmt;
    }

    // 2. Collect task IDs from the forecast
    const taskIds = new Set<string>();
    const collectTasks = (
      stagesAndTasks: { Tasks: { taskId: string }[] }[]
    ) => {
      stagesAndTasks?.forEach((s) => {
        s.Tasks?.forEach((t) => taskIds.add(t.taskId));
      });
    };
    collectTasks(forecast.Groundworks?.StagesAndTasks ?? []);
    collectTasks(forecast.RoadAndSewers?.StagesAndTasks ?? []);

    // 3. Compute budget from central repo by summing each selected task's own
    //    costBreakdown. This is TASK-level and must mirror ForecastVsBudget's
    //    `budgetTotals` memo so the card variance equals the summary table's
    //    total-row variance (see CLAUDE.md → "Forecasting Budget Calculation").
    //    Do NOT revert to stage-level (whole-stage cost when any task matches) —
    //    that over-counts unselected tasks and diverges from the table.
    let budgetTotal = 0;

    const sumMatchingTasks = (
      stages: {
        tasks: {
          taskId: string;
          costBreakdown?: {
            labour_rate: number;
            plant_rate: number;
            material_rate: number;
            subcontractor_rate: number;
          } | null;
        }[];
      }[]
    ) => {
      stages?.forEach((stage) => {
        stage.tasks?.forEach((task) => {
          if (taskIds.has(task.taskId) && task.costBreakdown) {
            budgetTotal +=
              task.costBreakdown.labour_rate +
              task.costBreakdown.plant_rate +
              task.costBreakdown.material_rate +
              task.costBreakdown.subcontractor_rate;
          }
        });
      });
    };

    projectCentralRepoData.groundworks?.plots?.forEach((plot) =>
      sumMatchingTasks(plot.stages)
    );
    projectCentralRepoData.roadsAndSewers?.runs?.forEach((run) =>
      sumMatchingTasks(run.stages)
    );

    // Add variation to budget total
    budgetTotal += variationAmt;

    return budgetTotal - forecastTotal;
  };

  const renderMonthButton = (btn: IMonthButton) => {
    const monthForecast = allForecastsData?.Forecasts?.find(
      (f) => f.Month === btn.value
    );
    const hasForecast = !!monthForecast;

    let forecastedAmt = 0;

    if (monthForecast?.Forecast?.BudgetForecasts) {
      const bf = monthForecast.Forecast.BudgetForecasts;
      forecastedAmt +=
        bf.BudgetLabourCosts?.reduce((sum, item) => sum + item.Amount, 0) ?? 0;
      forecastedAmt +=
        bf.BudgetPlantCosts?.reduce((sum, item) => sum + item.Amount, 0) ?? 0;
      forecastedAmt += bf.BudgetMaterialCost?.Amount ?? 0;
      forecastedAmt +=
        bf.BudgetSubContractorCost?.reduce(
          (sum, item) => sum + item.Amount,
          0
        ) ?? 0;
    }

    if (monthForecast?.Forecast?.Variations) {
      forecastedAmt += monthForecast.Forecast.Variations.reduce(
        (sum, v) => sum + (v.UtilizedAmount > 0 ? v.UtilizedAmount : 0),
        0
      );
    }

    const variance = monthForecast ? computeMonthVariance(monthForecast) : null;

    const isSelected = selectedMonth === btn.value;
    const isCurrentMonth = btn.value === dayjs().format("YYYY-MM");

    // Visual hierarchy: selection is the loudest signal (app primary blue, ring +
    // tinted fill), forecast status is a small dot, and "current month" is a
    // quiet pill — so the card being forecasted for is never ambiguous.
    return (
      <div
        key={btn.value}
        role="button"
        aria-pressed={isSelected}
        onClick={() => handleMonthClick(btn.value)}
        className={classNames(
          "cursor-pointer p-4 rounded-2xl border border-solid transition-all",
          isSelected
            ? "bg-blue-50 border-blue-500 ring-1 ring-blue-500 shadow-md"
            : hasForecast
              ? "bg-[hsl(131,85%,94%)] border-0"
              : isCurrentMonth
                ? "bg-[hsl(42,97%,95%)] border-0"
                : "bg-white border-slate-200 hover:border-slate-300"
        )}
      >
        <div className="flex flex-wrap justify-between gap-x-3 gap-y-1">
          <div className="text-base">
            <p
              className={classNames("flex items-center gap-1.5 font-medium", {
                "text-blue-700": isSelected,
                "text-slate-800": !isSelected
              })}
            >
              <span
                className={classNames("inline-block h-2 w-2 rounded-full", {
                  "bg-green-500": hasForecast,
                  "bg-slate-300": !hasForecast
                })}
              />
              {btn.month}
            </p>
            <p className="text-slate-500 text-sm">{btn.year}</p>
          </div>
          {isCurrentMonth && (
            <div className="self-start bg-amber-100 text-amber-800 rounded-full text-xs font-medium px-2 py-0.5">
              <span>Current</span>
            </div>
          )}
        </div>
        <div className="mt-2.5 text-sm">
          <div className="flex flex-wrap justify-between items-center gap-x-2">
            <span className="text-slate-500">Forecasted:</span>
            {allForecastsLoading ? (
              <ValueSkeleton />
            ) : (
              <span className="text-slate-800 font-medium">
                {forecastedAmt > 0
                  ? "£" + formatNumberToGB(forecastedAmt)
                  : "-"}
              </span>
            )}
          </div>
          <div className="flex flex-wrap justify-between items-center gap-x-2">
            <span className="text-slate-500">Variance:</span>
            {allForecastsLoading || projectCentralRepoLoading ? (
              <ValueSkeleton />
            ) : (
              <span
                className={classNames("font-medium", {
                  "text-green-600": variance !== null && variance > 0,
                  "text-red-500": variance !== null && variance < 0,
                  "text-slate-800": variance === null || variance === 0
                })}
              >
                {variance !== null
                  ? "£" + formatNumberToGB(Math.abs(variance))
                  : "-"}
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const forecastNotificationFactory = () => {
    if (monthForecastLoading) {
      return (
        <div className="mt-5 p-4 rounded-2xl bg-slate-50">
          <Skeleton
            active
            avatar={{ shape: "square", size: 48 }}
            title={{ width: "40%" }}
            paragraph={{ rows: 1, width: "60%" }}
          />
        </div>
      );
    }

    if (
      isPastMonth(selectedMonth) &&
      monthForecastData &&
      !features.allowPastMonthForecasting
    ) {
      return (
        <div className="mt-5 p-4 rounded-2xl bg-[#fff7e6] border border-solid border-[#ffa940] flex items-center gap-4">
          <span className="h-12 w-12 bg-white rounded-2xl inline-flex justify-center items-center">
            <LockOutlined className="text-[18px] text-[#fa8c16]" />
          </span>
          <div>
            <p className="text-[#1e1e1e] text-base font-semibold">
              Past Month Forecast — {dayjs(selectedMonth).format("MMMM YYYY")}
            </p>
            <p className="mt-1 text-[#787878] text-sm">
              This is a past month and cannot be reforecasted. You can view the
              forecast details below.
            </p>
          </div>
        </div>
      );
    }

    if (isLatestVersionSelected === false) {
      return (
        <div className="mt-5 p-4 rounded-2xl bg-[#fff7e6] flex items-center gap-4">
          <span className="h-12 w-12 bg-white rounded-2xl inline-flex justify-center items-center">
            <InfoCircleOutlined className="text-[18px] text-[#fa8c16]" />
          </span>
          <div>
            <p className="text-[#1e1e1e] text-base font-semibold">
              Viewing an Older Forecast Version
            </p>
            <p className="mt-1 text-[#787878] text-sm">
              Older versions are read-only and cannot be reforecasted. Switch to
              the latest version to reforecast.
            </p>
          </div>
          <div className="flex-1 flex justify-end">
            <Button
              disabled
              size="large"
              type="default"
              icon={<ReloadOutlined />}
              className=""
            >
              Reforecast
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="mt-5 p-4 rounded-2xl bg-[#e6f6fe66] flex items-center gap-4">
        <span className="h-12 w-12 bg-white rounded-2xl inline-flex justify-center items-center">
          <CalendarOutlined className="text-[18px] text-[#167ef9]" />
        </span>
        <div>
          <p className="text-[#1e1e1e] text-base font-semibold">
            {monthForecast404
              ? "No Forecast Found for"
              : "Existing Forecast Found for"}
            &nbsp;
            {dayjs(selectedMonth).format("MMMM YYYY")}
          </p>
          <p className="mt-1 text-[#787878] text-sm">
            {monthForecast404
              ? 'Click "Create Forecast" to create a new forecast version.'
              : monthForecastData?.isLocked
                ? "Forecast is locked."
                : 'Click "Reforecast" to create a new forecast version.'}
          </p>
        </div>
        <div className="flex-1 flex justify-end">
          {monthForecast404 && (
            <Button
              size="large"
              type="primary"
              onClick={handleCreateForecastClick}
              // className="bg-[#167ef9]"
            >
              Create Forecast
            </Button>
          )}

          {monthForecastData && !monthForecastData.isLocked && (
            <Button
              size="large"
              type="primary"
              icon={<ReloadOutlined />}
              onClick={() =>
                navigate(
                  `/forecasting/${encodeURIComponent(selectedProject?.opportunityNo)}/${encodeURIComponent(selectedProject?.description)}/${selectedMonth}`
                )
              }
              // className="bg-[#167ef9]"
            >
              Reforecast
            </Button>
          )}

          {monthForecastData &&
            monthForecastData.isLocked &&
            user?.roleDisplayName === "Commercial Director" && (
              <Button
                size="large"
                type="primary"
                icon={<UnlockOutlined />}
                onClick={() => {
                  Modal.confirm({
                    title: "Confirmation",
                    content: "Are you sure you want to unlock this forecast?",
                    onOk: async () => {
                      await lockUnlockForecast.mutateAsync({
                        opportunityNo: selectedProject?.opportunityNo,
                        projectName: selectedProject?.description,
                        date: selectedMonth,
                        payload: {
                          isLocked: false,
                          lockedBy: user?.email || "",
                          lockReason: ""
                        }
                      });
                    }
                  });
                }}
                // className="bg-[#167ef9]"
              >
                Unlock Forecast
              </Button>
            )}

          {monthForecastData &&
            monthForecastData.isLocked &&
            user?.roleDisplayName !== "Commercial Director" && (
              <Button
                disabled
                size="large"
                type="primary"
                icon={<ReloadOutlined />}
                // className="bg-[#167ef9]"
              >
                Reforecast
              </Button>
            )}
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <PageHeader
        title="Monthly Forecasting"
        description="Create and manage monthly forecasts with detailed cost breakdown"
      />

      <div className="mt-6 p-4 bg-white rounded-lg">
        <Select
          size="large"
          placeholder="Search by project name..."
          showSearch
          allowClear
          value={selectedProject}
          onChange={(_, obj) => {
            if (obj && typeof obj === "object" && !Array.isArray(obj)) {
              storage.setProject(`${obj.no}~~${obj.description}`);
              setSelectedProject(obj);
            } else {
              storage.setProject("");
              setSelectedProject(null);
            }

            if (intendedProject) {
              setSearchParams((prev) => {
                prev.delete("project");
                return prev;
              });
            }
          }}
          options={projectsData?.value?.reduce((acc: ISelectOption[], cur) => {
            if (
              cur?.jobPostingGroup &&
              cur.jobPostingGroup === user?.regionCode
            ) {
              acc.push({
                key: cur.no,
                value: cur.no,
                label: `${cur.no} - ${cur.description}`,
                no: cur.no,
                opportunityNo: cur.opportunityNo,
                description: cur.description
              });
            }
            return acc;
          }, [])}
          optionFilterProp="label"
          loading={projectsLoading}
          className="min-w-[400px]"
        />
      </div>

      {!selectedProject && (
        <div className="mt-6 p-4 bg-white rounded-lg">
          <div className="text-center py-12">
            <ProjectOutlined className="text-6xl text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              Select a project to get started
            </h3>
            <p className="text-gray-500">
              Choose a project from the dropdown above to begin forecasting.
            </p>
          </div>
        </div>
      )}

      {selectedProject && (
        <div className="mt-6 p-4 bg-white rounded-lg">
          <div className="pb-4 border-0 border-b border-solid border-b-[#e1e6ef] flex gap-10">
            <div className="flex gap-2">
              <span className="self-start p-2 inline-flex justify-center items-center bg-[#e6f6fe] rounded-lg">
                <CalendarOutlined className="text-lg text-[#167ef9]" />
              </span>
              <div>
                <p className="text-base text-[#1e293b] font-semibold">
                  Select Forecast Month
                </p>
                <p className="text-sm text-[#64748b]">
                  Choose a month to create a new forecast or reforecast an
                  existing one
                </p>
              </div>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-x-2">
            <button
              onClick={() => setMonthOffset((prev) => prev - 6)}
              className="border-none h-[25px] w-[25px] bg-[#f1f3f9] rounded-md cursor-pointer"
            >
              <LeftOutlined />
            </button>
            <div className="flex-1 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {monthButtons.map((btn) => renderMonthButton(btn))}
            </div>
            <button
              disabled={monthOffset >= 0}
              onClick={() => {
                if (monthOffset >= 0) return;
                setMonthOffset((prev) => prev + 6);
              }}
              className="border-none h-[25px] w-[25px] bg-[#f1f3f9] rounded-md cursor-pointer disabled:cursor-not-allowed"
            >
              <RightOutlined />
            </button>
          </div>
          {/* Legends */}
          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-slate-600">
            <p className="text-slate-500">Index:</p>
            <div className="flex items-center gap-1.5">
              <span className="inline-block h-3 w-4 rounded bg-blue-50 border border-solid border-blue-500 ring-1 ring-blue-500" />
              <span>Selected</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="inline-block h-2 w-2 bg-green-500 rounded-full" />
              <span>Forecasted</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="inline-block h-2 w-2 bg-slate-300 rounded-full" />
              <span>Not Forecasted</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="bg-amber-100 text-amber-800 rounded-full text-xs font-medium px-2 py-0.5">
                Current
              </span>
              <span>This month</span>
            </div>
          </div>

          {forecastNotificationFactory()}
        </div>
      )}

      {selectedProject && monthForecastLoading && (
        <div className="mt-6 p-4 bg-white rounded-lg">
          <Skeleton active paragraph={{ rows: 6 }} />
        </div>
      )}

      {selectedProject && monthForecastData && (
        <div className="mt-6">
          <ForecastVsBudget
            selectedVersion={selectedVersion}
            setSelectedVersion={setSelectedVersion}
            setIsLatestVersionSelected={setIsLatestVersionSelected}
            setLatestVersion={setLatestVersion}
            selectedProject={selectedProject}
            selectedMonth={selectedMonth}
            projectCentralRepoLoading={projectCentralRepoLoading}
            projectCentralRepoData={projectCentralRepoData}
          />
        </div>
      )}

      {selectedProject && monthForecastData && projectCentralRepoData && (
        <div className="mt-6">
          <TasksStageView
            latestVersion={latestVersion}
            selectedProject={selectedProject}
            selectedMonth={selectedMonth}
            monthForecastData={monthForecastData}
            projectCentralRepoLoading={projectCentralRepoLoading}
            projectCentralRepoData={projectCentralRepoData}
          />
        </div>
      )}
      <CarryForwardModal
        open={!!carryForward}
        previousMonth={previousMonth.format("MMMM YYYY")}
        incompleteTasks={carryForward?.tasks ?? []}
        costTotals={carryForward?.costTotals ?? null}
        onConfirm={handleConfirmCarryForward}
        onClose={() => setCarryForward(null)}
      />
    </Layout>
  );
};

export default ForecastingOverviewPage;
